import {
  INCIDENT_CATEGORIES,
  INCIDENT_STATUSES,
  canTransition,
  type Incident,
  type IncidentStatus,
  type IncidentType,
  type SurfaceDef,
} from "@feedback-hub/contract/browser";

export const ADMIN_STATUS_LABELS: Record<IncidentStatus, string> = {
  new: "New",
  triaged: "Triaged",
  in_progress: "In progress",
  fix_deployed: "Fix deployed",
  verified: "Verified",
  closed: "Closed",
  wont_fix: "Won't fix",
  duplicate: "Duplicate",
  spam: "Spam",
};

export const TYPE_LABELS: Record<IncidentType, string> = {
  bug: "Bug",
  feature: "Feature",
  contact: "Contact",
  rating: "Rating",
  nps: "NPS",
};

export function categoryLabel(categoryId: string | undefined): string {
  if (!categoryId) return "—";
  const match = INCIDENT_CATEGORIES.find((c) => c.id === categoryId);
  return match ? match.label : categoryId;
}

export function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export function formatAge(iso: string, now = Date.now()): string {
  const ms = now - Date.parse(iso);
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d`;
}

export function formatDuration(ms: number | null): string {
  if (ms == null) return "—";
  if (ms < 60_000) return `${Math.round(ms / 1000)}s`;
  if (ms < 3_600_000) return `${Math.round(ms / 60_000)}m`;
  if (ms < 86_400_000) return `${(ms / 3_600_000).toFixed(1)}h`;
  return `${(ms / 86_400_000).toFixed(1)}d`;
}

export function formatRate(rate: number | null): string {
  if (rate == null) return "insufficient data";
  return `${(rate * 100).toFixed(1)}%`;
}

export function bodyPreview(body: string, max = 120): string {
  const oneLine = body.replace(/\s+/g, " ").trim();
  if (oneLine.length <= max) return oneLine;
  return `${oneLine.slice(0, max - 1)}…`;
}

/** Legal next statuses for the dropdown (current value always included). */
export function nextStatusOptions(current: IncidentStatus): IncidentStatus[] {
  const options = INCIDENT_STATUSES.filter(
    (s) => s === current || canTransition(current, s),
  );
  return options;
}

/**
 * Hub admin has no client surface registry (contract: hub registers none). Build a
 * minimal SurfaceDef from the incident so the prompt still renders.
 */
export function surfaceForIncident(incident: Incident): SurfaceDef {
  const surfaceId = incident.context.surfaceId || "unknown";
  return {
    label: surfaceId,
    promptFraming: `Surface ${surfaceId}. Use the captured context below to locate the fault.`,
    likelyFiles: [],
  };
}

export function logDeidentifyOff(incident: Incident): void {
  // improvement-prompt.md §4: log when the reviewer deliberately disables de-identify.
  console.info(
    JSON.stringify({
      event: "deidentify_toggled_off",
      ref: incident.ref,
      incidentId: incident.incidentId,
      appId: incident.appId,
      at: new Date().toISOString(),
    }),
  );
}
